import React, { ChangeEvent, FC, useEffect, useRef } from 'react';
import { Box, getSemanticValue, Spaces, Text, TrashIcon, UploadIcon, WarningIcon } from '@freenow/wave';
import styled from 'styled-components';
import { useDropTarget } from '../hooks/useDropTarget';

interface SingleFilePickerProps {
    id?: string;
    file?: File;
    info: string;
    multipleFilesError: string;
    onFileSelected: (file?: File) => void;
    accept?: string;
    disabled?: boolean;
}

const FilePickerBox = styled<{ isDragging: boolean; isDraggingNotExactlyOne: boolean; isDisabled?: boolean }>(Box)`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    height: ${Spaces[9]};
    gap: ${Spaces[1]};
    cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
    opacity: ${({ isDisabled }) => (isDisabled ? 0.7 : 1)};
    color: ${({ isDraggingNotExactlyOne }) =>
        isDraggingNotExactlyOne
            ? getSemanticValue('foreground-on-background-danger')
            : getSemanticValue('foreground-on-background-neutral')};
    background-color: ${({ isDraggingNotExactlyOne, isDragging }) =>
        isDraggingNotExactlyOne
            ? getSemanticValue('background-element-danger-emphasized')
            : isDragging
              ? getSemanticValue('background-element-accent-faded')
              : getSemanticValue('background-element-neutral-emphasized')};
    border-width: ${({ isDraggingNotExactlyOne }) => (isDraggingNotExactlyOne ? '0px' : '1px')};
    border-style: dashed;
    border-color: ${({ isDragging }) =>
        isDragging ? getSemanticValue('border-accent-faded') : getSemanticValue('border-neutral-emphasized')};
    border-radius: 4px;
`;

const DropTarget = styled.div`
    position: absolute;
    width: 100%;
    height: 100%;
`;

export const SingleFilePicker: FC<SingleFilePickerProps> = ({
    id,
    info,
    multipleFilesError,
    onFileSelected,
    file,
    accept,
    disabled = false,
}) => {
    const dropTargetRef = useRef<HTMLElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (inputRef.current) {
            const transfer = new DataTransfer();
            if (file) transfer.items.add(file);
            inputRef.current.files = transfer.files;
        }
    }, [file, inputRef]);

    const { isDragging, itemCount } = useDropTarget(
        (files) => {
            if (disabled) return;
            if (files?.length === 1) {
                onFileSelected(files[0]);
            }
        },
        [dropTargetRef, onFileSelected],
    );
    const isDraggingNotExactlyOne = isDragging && itemCount !== 1;

    const onClick = () => {
        if (disabled) return;
        if (file) {
            onFileSelected(undefined);
        } else {
            inputRef.current?.click();
        }
    };

    const onChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (disabled) return;
        onFileSelected(event.target.files?.[0]);
    };
    return (
        <FilePickerBox
            isDragging={isDragging}
            isDraggingNotExactlyOne={isDraggingNotExactlyOne}
            isDisabled={disabled}
            onClick={onClick}
        >
            <input
                type="file"
                id={id}
                name={id}
                accept={accept}
                onChange={onChange}
                ref={inputRef}
                multiple={false}
                disabled={disabled}
                style={{ overflow: 'hidden', width: 0, height: 0 }}
            />
            {isDraggingNotExactlyOne ? (
                <>
                    <WarningIcon />
                    <Text>{multipleFilesError}</Text>
                </>
            ) : file ? (
                <>
                    <Text>{file.name}</Text>
                    <TrashIcon />
                </>
            ) : (
                <>
                    <UploadIcon />
                    <Text>{info}</Text>
                </>
            )}
            <DropTarget ref={dropTargetRef} />
        </FilePickerBox>
    );
};
